'use client';

import { QueryKeys } from '@/constants/query-keys';
import { AnalyticsEvent } from '@/enums/analytics-type';
import { emailSubscriptionApiService } from '@/services/(frontend)/profile/email-subscription-api.service';
import { addToPagedList, updatePagedList, upsertPagedList } from '@/utils/query-update-utils';
import { EmailSubscription, EmailSubscriptionData, PagedListOfEmailSubscription } from '@cinemark/profile-api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';

export type UpdateSubscriptionWithAnalyticsType = {
  analyticsEvent?: AnalyticsEvent;
  queryKey: readonly string[];
  subscribed: boolean;
};

type UpdateSubscriptionType = {
  analyticsEvent?: AnalyticsEvent;
  emailSubscriptionData: EmailSubscriptionData;
  emailSubscriptionId: number;
};

export const useEmailSubscriptions = (): ReturnType<typeof useQuery<PagedListOfEmailSubscription>> => {
  const updateQueryStaleTime = useDynamicStaleTime();

  return useQuery<PagedListOfEmailSubscription>({
    queryFn: async () => {
      const response = await emailSubscriptionApiService.subscriptions();

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.user.emailSubscriptions, response.headers);
      }

      return response.data;
    },
    queryKey: QueryKeys.user.emailSubscriptions,
  });
};

export const useUpdateSubscription = (): ReturnType<typeof useMutation<void, unknown, UpdateSubscriptionType, UpdateSubscriptionWithAnalyticsType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ emailSubscriptionData, emailSubscriptionId }: UpdateSubscriptionType) =>
      await emailSubscriptionApiService.updateSubscription(emailSubscriptionId, emailSubscriptionData),
    onMutate: ({ analyticsEvent, emailSubscriptionData }) => {
      return { analyticsEvent, queryKey: QueryKeys.user.emailSubscriptions, subscribed: emailSubscriptionData.subscribed };
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<PagedListOfEmailSubscription>(QueryKeys.user.emailSubscriptions, oldData =>
        updatePagedList(
          oldData,
          sub => sub.id === variables.emailSubscriptionId,
          sub => ({ ...sub, ...variables.emailSubscriptionData })
        )
      );
    },
  });
};

type UpsertSubscriptionType = {
  analyticsEvent?: AnalyticsEvent;
  emailSubscriptionData: EmailSubscriptionData;
};

export const useUpsertSubscription = ():
ReturnType<typeof useMutation<EmailSubscription, unknown, UpsertSubscriptionType, UpdateSubscriptionWithAnalyticsType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ emailSubscriptionData }: UpsertSubscriptionType) => await emailSubscriptionApiService.upsertSubscription(emailSubscriptionData),
    onMutate: ({ analyticsEvent, emailSubscriptionData }) => {
      return { analyticsEvent, queryKey: QueryKeys.user.emailSubscriptions, subscribed: emailSubscriptionData.subscribed };
    },
    onSuccess: (newSubscription) => {
      queryClient.setQueryData<PagedListOfEmailSubscription>(QueryKeys.user.emailSubscriptions, oldData =>
        upsertPagedList(
          oldData,
          newSubscription,
          sub => sub.id === newSubscription.id
        )
      );
    },
  });
};

type CreateGuestSubscriptionType = {
  email: string;
  emailSubscriptionData: EmailSubscriptionData;
};

export const useCreateGuestSubscription = (): ReturnType<typeof useMutation<EmailSubscription, unknown, CreateGuestSubscriptionType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, emailSubscriptionData }: CreateGuestSubscriptionType) =>
      await emailSubscriptionApiService.createGuestSubscription(email, emailSubscriptionData),
    onSuccess: (newSubscription) => {
      queryClient.setQueryData<PagedListOfEmailSubscription>(QueryKeys.user.emailSubscriptions, oldData =>
        addToPagedList(oldData, newSubscription)
      );
    },
  });
};
